/**
 * CBBHeadshot Component
 *
 * Displays college basketball player headshots from ESPN CDN
 * with automatic fallback handling. Mirrors the MLBHeadshot.js pattern.
 *
 * ESPN CDN is CORS-enabled, so images load directly in the browser.
 *
 * @example
 * // By ESPN ID
 * <CBBHeadshot espnId={5105600} size="MEDIUM" />
 *
 * // By player name (shows initials as fallback)
 * <CBBHeadshot playerName="Cooper Flagg" size="LARGE" />
 *
 * // Card variant
 * <CBBHeadshotCard espnId={5105600} playerName="Cooper Flagg" />
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  getCBBHeadshotUrl,
  getCBBPlaceholderUrl,
  CBB_HEADSHOT_SIZES,
} from '../lib/cbbHeadshotService';

/**
 * Extract initials from a player name.
 * @param {string} name - Full player name
 * @returns {string} - Up to 2 initials
 */
function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '');
}

/**
 * CBBHeadshot - Renders college basketball player headshots with fallback support
 *
 * @param {Object} props
 * @param {number|string} [props.espnId] - ESPN player ID (primary identifier)
 * @param {string} [props.playerName] - Player name for alt text and initials fallback
 * @param {string} [props.size='MEDIUM'] - Size preset: SMALL, MEDIUM, LARGE
 * @param {string} [props.gender='mens'] - 'mens' or 'womens'
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles
 * @param {boolean} [props.showPlaceholder=true] - Show initials placeholder on error
 * @param {Function} [props.onLoad] - Callback when image loads
 * @param {Function} [props.onError] - Callback when all fallbacks fail
 */
const CBBHeadshot = ({
  espnId,
  playerName,
  size = 'MEDIUM',
  gender = 'mens',
  className = '',
  style = {},
  showPlaceholder = true,
  onLoad,
  onError,
}) => {
  const [imageState, setImageState] = useState('loading'); // loading, loaded, fallback, error
  const [currentSrc, setCurrentSrc] = useState(null);

  // Generate the primary image URL
  const getPrimaryUrl = useCallback(() => {
    if (espnId) {
      return getCBBHeadshotUrl(espnId, { size, gender });
    }
    return null;
  }, [espnId, size, gender]);

  // Initialize current source on mount / prop changes
  useEffect(() => {
    const url = getPrimaryUrl();
    if (url) {
      setCurrentSrc(url);
      setImageState('loading');
    } else if (showPlaceholder) {
      // No espnId — go straight to initials
      setImageState('error');
      setCurrentSrc(null);
    }
  }, [getPrimaryUrl, showPlaceholder]);

  // Handle successful image load
  const handleLoad = useCallback(() => {
    setImageState('loaded');
    onLoad?.();
  }, [onLoad]);

  // Handle image load error with fallback chain
  const handleError = useCallback(() => {
    if (imageState !== 'error') {
      // Try ESPN placeholder (generic silhouette) if not already on it
      const placeholder = getCBBPlaceholderUrl({ size });
      if (currentSrc !== placeholder) {
        setCurrentSrc(placeholder);
        setImageState('fallback');
        return;
      }
    }
    // All image sources failed — show initials
    setImageState('error');
    setCurrentSrc(null);
    onError?.();
  }, [imageState, currentSrc, size, onError]);

  // Get dimensions for the size
  const dimensions = CBB_HEADSHOT_SIZES[size] || CBB_HEADSHOT_SIZES.MEDIUM;

  // Generate alt text
  const altText = playerName ? `${playerName} headshot` : 'Player headshot';

  // Render initials fallback
  if (imageState === 'error' || !currentSrc) {
    if (!showPlaceholder) return null;

    return (
      <div
        className={`cbb-headshot-initials flex items-center justify-center rounded-full bg-gradient-to-br from-gray-600 to-gray-800 text-white/60 font-bold select-none ${className}`}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          fontSize: Math.round(dimensions.width * 0.35),
          ...style,
        }}
        title={playerName || 'Unknown player'}
      >
        {getInitials(playerName)}
      </div>
    );
  }

  return (
    <div
      className={`cbb-headshot-container relative overflow-hidden ${className}`}
      style={{
        width: dimensions.width,
        height: dimensions.height,
        ...style,
      }}
    >
      {/* Loading skeleton */}
      {imageState === 'loading' && (
        <div
          className="absolute inset-0 bg-gray-700 animate-pulse rounded-full"
          style={{ width: dimensions.width, height: dimensions.height }}
        />
      )}

      {/* Main image */}
      <img
        src={currentSrc}
        alt={altText}
        onLoad={handleLoad}
        onError={handleError}
        className={`
          cbb-headshot-image
          rounded-full
          object-cover
          transition-opacity
          duration-200
          ${imageState === 'loaded' || imageState === 'fallback' ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          width: dimensions.width,
          height: dimensions.height,
        }}
        loading="lazy"
      />
    </div>
  );
};

/**
 * CBBHeadshotInline - Smaller inline version for text contexts
 */
export const CBBHeadshotInline = (props) => (
  <CBBHeadshot
    {...props}
    size="SMALL"
    className={`inline-block align-middle ${props.className || ''}`}
  />
);

/**
 * CBBHeadshotCard - Card-sized version with name label
 */
export const CBBHeadshotCard = ({
  espnId,
  playerName,
  displayName,
  size = 'MEDIUM',
  ...props
}) => (
  <div className="cbb-headshot-card flex flex-col items-center gap-2">
    <CBBHeadshot
      espnId={espnId}
      playerName={playerName}
      size={size}
      {...props}
    />
    {(displayName || playerName) && (
      <span className="text-sm font-medium text-gray-300">
        {displayName || playerName}
      </span>
    )}
  </div>
);

/**
 * Hook for preloading CBB headshots
 */
export const usePreloadCBBHeadshots = (espnIds, options = {}) => {
  useEffect(() => {
    if (!espnIds?.length) return;

    espnIds.forEach(espnId => {
      const img = new Image();
      img.src = getCBBHeadshotUrl(espnId, options);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(espnIds)]);
};

export default CBBHeadshot;
